import { CandidatLink } from './candidat/CandidatLink';
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { GraduationCap, CalendarClock, Plus, X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isCert = (code) => (code || '').toUpperCase().startsWith('CERT');

export const DispatchModulesSection = ({ cahier, setCahier, candidats, dayModules, allModules, dossier, canEdit, candidatsEnAft = {} }) => {
  const [certDialog, setCertDialog] = useState(null); // { candidat, module }
  const [certOption, setCertOption] = useState('date');
  const [certDate, setCertDate] = useState('');
  const [certAftId, setCertAftId] = useState('');
  const [afts, setAfts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addingFor, setAddingFor] = useState(null); // candidat_id en cours d'ajout de module

  const dispatch = cahier.dispatch_modules || [];
  const getEntry = (cid, mid) => dispatch.find(e => e.candidat_id === cid && e.module_id === mid);
  const isSuivi = (cid, mid) => {
    const e = getEntry(cid, mid);
    return e ? e.suivi !== false : true;
  };

  const setEntry = (entry) => {
    setCahier(prev => {
      const list = (prev.dispatch_modules || []).filter(e => !(e.candidat_id === entry.candidat_id && e.module_id === entry.module_id));
      return { ...prev, dispatch_modules: [...list, entry] };
    });
  };
  const removeEntry = (cid, mid) => {
    setCahier(prev => ({
      ...prev,
      dispatch_modules: (prev.dispatch_modules || []).filter(e => !(e.candidat_id === cid && e.module_id === mid)),
    }));
  };

  const openCertDialog = async (candidat, module) => {
    setCertOption('date');
    setCertDate('');
    setCertAftId('');
    setCertDialog({ candidat, module });
    try {
      const res = await axios.get(`${API_URL}/modules/${module.module_id}/afts`);
      setAfts(res.data || []);
    } catch { setAfts([]); }
  };

  const handleToggle = (candidat, module) => {
    if (!canEdit) return;
    const cid = candidat.id;
    if (isSuivi(cid, module.module_id)) {
      if (isCert(module.code)) {
        openCertDialog(candidat, module);
      } else {
        setEntry({ candidat_id: cid, module_id: module.module_id, suivi: false });
      }
    } else {
      removeEntry(cid, module.module_id);
    }
  };

  const confirmCert = async () => {
    const { candidat, module } = certDialog;
    setSaving(true);
    try {
      if (certOption === 'date') {
        if (!certDate) { toast.error('Choisissez une date de report'); setSaving(false); return; }
        setEntry({ candidat_id: candidat.id, module_id: module.module_id, suivi: false, report_type: 'date', report_date: certDate });
        toast.success(`Passage de ${module.code} reporté au ${new Date(certDate + 'T00:00:00').toLocaleDateString('fr-FR')} pour ${candidat.nom} ${candidat.prenom}`);
      } else if (certOption === 'aft_existante') {
        const aft = afts.find(a => a.id === certAftId);
        if (!aft) { toast.error('Choisissez une session AFT'); setSaving(false); return; }
        await axios.post(`${API_URL}/dossiers/${aft.id}/inscrire-candidat`, { candidat_id: candidat.id });
        setEntry({ candidat_id: candidat.id, module_id: module.module_id, suivi: false, report_type: 'aft', aft_id: aft.id, aft_code: aft.code });
        toast.success(`${candidat.nom} ${candidat.prenom} inscrit sur la session ${aft.code}`);
      }
      setCertDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // Modules supplémentaires appliqués par le formateur (hors modules du jour)
  const dayModuleIds = new Set(dayModules.map(m => m.module_id));
  const extraOf = (cid) => dispatch.filter(e => e.candidat_id === cid && e.suivi === true && !dayModuleIds.has(e.module_id));

  return (
    <div data-testid="dispatch-modules-section">
      <p className="text-xs text-slate-500 mb-3">
        Tous les candidats sont positionnés par défaut sur les modules de la journée. Décochez un module si un candidat ne le suit pas.
        {Object.keys(candidatsEnAft).length > 0 && <span className="block mt-1 text-cyan-700" data-testid="dispatch-en-aft-note">{Object.keys(candidatsEnAft).length} candidat(s) délégué(s) en AFT ce jour ({[...new Set(Object.values(candidatsEnAft).map(a => a.aft_code))].join(', ')}) : non dispatchés ici. Indiquez le module suivi par les candidats restés sur l'AF via « + module ».</span>}
        Pour un module certifiant (CERT), un report ou une action de certification (AFT) vous sera proposé.
      </p>
      {dayModules.length === 0 ? (
        <p className="text-sm text-slate-400 italic text-center py-4">Aucun module défini pour cette journée (renseignez les modules réalisés)</p>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100 text-slate-500 uppercase tracking-wide">
                <th className="px-3 py-2 text-left font-medium min-w-[160px]">Candidat</th>
                {dayModules.map(m => (
                  <th key={m.module_id} className="px-3 py-2 text-center font-medium min-w-[120px]">
                    <div className="flex flex-col items-center gap-0.5 normal-case">
                      <span className="line-clamp-2">{m.titre || m.code}</span>
                      {isCert(m.code) && (
                        <span className="text-[9px] font-bold text-rose-700 bg-rose-100 px-1 py-0.5 rounded">
                          <GraduationCap className="w-2.5 h-2.5 inline mr-0.5" />CERT
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2 text-left font-medium min-w-[150px]">Autres modules</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {candidats.filter(c => !candidatsEnAft[c.id]).map(c => (
                <tr key={c.id} data-testid={`dispatch-row-${c.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800"><CandidatLink dossierId={dossier?.id} candidatId={c.id}>{c.nom} {c.prenom}</CandidatLink></td>
                  {dayModules.map(m => {
                    const suivi = isSuivi(c.id, m.module_id);
                    const entry = getEntry(c.id, m.module_id);
                    return (
                      <td key={m.module_id} className="px-3 py-2 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <Checkbox
                            checked={suivi}
                            onCheckedChange={() => handleToggle(c, m)}
                            disabled={!canEdit}
                            data-testid={`dispatch-check-${c.id}-${m.module_id}`}
                          />
                          {!suivi && entry?.report_type === 'date' && (
                            <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700 bg-amber-50">
                              <CalendarClock className="w-2.5 h-2.5 mr-0.5" />
                              {new Date(entry.report_date + 'T00:00:00').toLocaleDateString('fr-FR')}
                            </Badge>
                          )}
                          {!suivi && entry?.report_type === 'aft' && (
                            <Badge variant="outline" className="text-[9px] border-cyan-300 text-cyan-700 bg-cyan-50" title="Session d'examen">
                              {entry.aft_code}
                            </Badge>
                          )}
                          {!suivi && !entry?.report_type && (
                            <span className="text-[9px] text-slate-400 italic">Ne suit pas</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-1">
                      {extraOf(c.id).map(e => {
                        const mod = allModules.find(m => m.id === e.module_id);
                        return (
                          <span key={e.module_id} className="inline-flex items-center gap-1 text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded">
                            {mod ? `${mod.code} ${mod.titre}`.slice(0, 30) : e.module_id.slice(0, 8)}
                            {canEdit && (
                              <button type="button" onClick={() => removeEntry(c.id, e.module_id)} className="text-indigo-400 hover:text-red-500">
                                <X className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </span>
                        );
                      })}
                      {canEdit && (addingFor === c.id ? (
                        <Select
                          value=""
                          onValueChange={(v) => {
                            setEntry({ candidat_id: c.id, module_id: v, suivi: true });
                            setAddingFor(null);
                          }}
                          open
                          onOpenChange={(o) => { if (!o) setAddingFor(null); }}
                        >
                          <SelectTrigger className="h-6 w-40 text-[10px]" data-testid={`dispatch-add-select-${c.id}`}>
                            <SelectValue placeholder="Module..." />
                          </SelectTrigger>
                          <SelectContent className="max-h-56">
                            {allModules
                              .filter(m => !dayModuleIds.has(m.id) && !extraOf(c.id).some(e => e.module_id === m.id))
                              .map(m => (
                                <SelectItem key={m.id} value={m.id} className="text-xs">{m.code} — {m.titre}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setAddingFor(c.id)}
                          className="inline-flex items-center gap-0.5 text-[10px] text-slate-400 hover:text-indigo-600 border border-dashed border-slate-300 hover:border-indigo-300 px-1.5 py-0.5 rounded transition-colors"
                          data-testid={`dispatch-add-btn-${c.id}`}
                        >
                          <Plus className="w-2.5 h-2.5" /> module
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialogue module certifiant décoché */}
      <Dialog open={!!certDialog} onOpenChange={(o) => !o && setCertDialog(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-cert-report">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <GraduationCap className="w-5 h-5 text-rose-600" />
              Passage de l'examen {certDialog?.module?.code}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            <span className="font-semibold">{certDialog?.candidat?.nom} {certDialog?.candidat?.prenom}</span> ne passe pas
            ce module certifiant aujourd'hui. Comment son passage est-il organisé ?
          </p>
          <RadioGroup value={certOption} onValueChange={setCertOption} className="space-y-2">
            <div className="flex items-start gap-2 p-2.5 rounded-lg border border-slate-200 hover:border-indigo-300">
              <RadioGroupItem value="date" id="opt-date" className="mt-0.5" data-testid="cert-opt-date" />
              <div className="flex-1">
                <Label htmlFor="opt-date" className="text-sm font-medium cursor-pointer">Programmé à une date ultérieure</Label>
                {certOption === 'date' && (
                  <Input type="date" value={certDate} onChange={(e) => setCertDate(e.target.value)} className="mt-2 h-8 text-xs" data-testid="cert-date-input" />
                )}
              </div>
            </div>
            <div className="flex items-start gap-2 p-2.5 rounded-lg border border-slate-200 hover:border-indigo-300">
              <RadioGroupItem value="aft_existante" id="opt-aft" className="mt-0.5" data-testid="cert-opt-aft" />
              <div className="flex-1">
                <Label htmlFor="opt-aft" className="text-sm font-medium cursor-pointer">Sur une action de certification existante (AFT)</Label>
                {certOption === 'aft_existante' && (
                  afts.length > 0 ? (
                    <Select value={certAftId} onValueChange={setCertAftId}>
                      <SelectTrigger className="mt-2 h-8 text-xs" data-testid="cert-aft-select"><SelectValue placeholder="Choisir une session..." /></SelectTrigger>
                      <SelectContent>
                        {afts.map(a => (
                          <SelectItem key={a.id} value={a.id} className="text-xs">
                            {a.code} — {a.date_debut ? new Date(a.date_debut + 'T00:00:00').toLocaleDateString('fr-FR') : ''} ({a.nb_candidats} candidat(s))
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-xs text-slate-400 italic mt-1">Aucune session AFT existante pour ce module</p>
                  )
                )}
              </div>
            </div>
            <p className="text-[11px] text-slate-400 italic px-1" data-testid="cert-no-create-note">La création d'une nouvelle AFT se fait depuis le fil chronologique de l'AF mère (administration). Le formateur peut uniquement rattacher un candidat à une AFT existante ou reporter la date.</p>
          </RadioGroup>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCertDialog(null)} data-testid="cert-cancel">Annuler</Button>
            <Button size="sm" onClick={confirmCert} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="cert-confirm">
              {saving ? '...' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
